import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Shield, Zap, Brain } from "lucide-react";
import NexoCore from "@/components/dashboard/NexoCore";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InlineSpinner } from "@/components/shared";
import { cn } from "@/lib/utils";

function FloatingOrb() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
      {/* Radial glows use brand tokens (cyan + purple) instead of Tailwind's legacy cyan-500/purple-500. */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full opacity-[0.06]"
        style={{ background: "radial-gradient(circle, #00D4FF 0%, transparent 70%)" }}
      />
      <div
        className="absolute w-[450px] h-[450px] rounded-full opacity-[0.05] translate-x-20 -translate-y-10"
        style={{ background: "radial-gradient(circle, #7A5CFF 0%, transparent 70%)" }}
      />
      <div className="relative -translate-y-12">
        <NexoCore state="idle" size={500} />
      </div>
    </div>
  );
}

function FeatureTag({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-surface-card-hover border border-subtle">
      <Icon className="w-3.5 h-3.5 text-brand-cyan" />
      <span className="text-caption text-foreground-muted">{text}</span>
    </div>
  );
}

export default function AuthPage() {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Per-field error drives the Input `variant="error"` focus ring.
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Bem-vindo de volta!");
      } else {
        if (!name.trim()) {
          setErrors({ name: true });
          toast.error("Preencha seu nome");
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          setErrors({ password: true });
          toast.error("Senha mínima: 6 caracteres");
          setLoading(false);
          return;
        }
        await register(name, email, password);
        toast.success("Conta criada!");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setName("");
    setEmail("");
    setPassword("");
    setErrors({});
  };

  // Shared input className so all 3 fields feel the same premium height/radius.
  const inputCls = "h-12 rounded-lg px-4 text-sm bg-surface-card-hover";

  return (
    <div className="relative min-h-screen flex bg-surface-canvas overflow-hidden" data-testid="auth-page">
      {/* UI-1.5 — Ambient layers globais */}
      <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
      <div
        className="absolute pointer-events-none"
        style={{
          top: 0, left: 0,
          width: "65%", height: "65%",
          background: "radial-gradient(circle at 25% 30%, rgba(0,212,255,0.10), transparent 55%)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: 0, right: 0,
          width: "50%", height: "50%",
          background: "radial-gradient(circle at 80% 70%, rgba(122,92,255,0.08), transparent 55%)",
        }}
        aria-hidden="true"
      />
      {/* Left Panel — Brand */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden flex-col justify-between p-12">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <FloatingOrb />

        {/* Logo top */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="flex items-center gap-3">
            <img
              src="/logo-nav.png"
              alt="Nexo"
              className="w-10 h-10 rounded-lg object-cover"
              style={{ boxShadow: "0 0 20px rgba(0, 212, 255, 0.25)" }}
            />
            <span className="text-foreground font-display text-xl font-bold tracking-wide">Nexo</span>
          </div>
        </motion.div>

        {/* Hero text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="relative z-10 max-w-md"
        >
          <h2 className="font-display text-hero text-foreground leading-tight mb-4">
            Seu assistente<br />
            <span className="bg-gradient-to-r from-brand-cyan to-brand-purple bg-clip-text text-transparent">
              pessoal inteligente
            </span>
          </h2>
          <p className="text-body text-foreground-subtle leading-relaxed mb-8">
            Organize tarefas, controle finanças, gerencie projetos e muito mais com inteligência artificial de última geração.
          </p>
          <div className="flex flex-wrap gap-2">
            <FeatureTag icon={Brain} text="IA Avançada" />
            <FeatureTag icon={Zap} text="Comando por Voz" />
            <FeatureTag icon={Shield} text="Dados Seguros" />
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="relative z-10"
        >
          <p className="text-micro text-foreground-disabled">Powered by OpenAI GPT · ElevenLabs Voice</p>
        </motion.div>
      </div>

      {/* Right Panel — Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative">
        <div
          className="absolute inset-0 opacity-40"
          style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0, 212, 255, 0.04) 0%, transparent 60%)" }}
        />

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.32 }}
          className="w-full max-w-sm relative z-10 glass-card-strong gradient-border-cyan rounded-2xl p-7 md:p-8"
        >
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logo-nav.png"
              alt="Nexo"
              className="inline-block w-14 h-14 rounded-2xl object-cover mb-3"
              style={{ boxShadow: "0 0 24px rgba(0, 212, 255, 0.3)" }}
            />
            <h1 className="text-h2 font-display text-foreground">Nexo</h1>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="font-display text-h2 text-foreground tracking-wide">
              {isLogin ? "Bem-vindo de volta" : "Criar conta"}
            </h2>
            <p className="text-caption text-foreground-subtle mt-1">
              {isLogin ? "Entre para continuar" : "Comece sua jornada"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div
                  key="name"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <label className="block text-micro text-foreground-subtle mb-1.5 uppercase tracking-wider font-medium">
                    Nome
                  </label>
                  <Input
                    data-testid="auth-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome"
                    variant={errors.name ? "error" : "default"}
                    className={inputCls}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="block text-micro text-foreground-subtle mb-1.5 uppercase tracking-wider font-medium">
                Email
              </label>
              <Input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                variant={errors.email ? "error" : "default"}
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-micro text-foreground-subtle mb-1.5 uppercase tracking-wider font-medium">
                Senha
              </label>
              <div className="relative">
                <Input
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isLogin ? "Sua senha" : "Mínimo 6 caracteres"}
                  required
                  variant={errors.password ? "error" : "default"}
                  className={cn(inputCls, "pr-12")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-subtle hover:text-foreground-muted transition-colors duration-fast ease-smooth"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              data-testid="auth-submit-button"
              type="submit"
              variant="primary"
              size="lg"
              disabled={loading}
              className="w-full mt-2 group"
            >
              {loading ? (
                <InlineSpinner />
              ) : (
                <>
                  {isLogin ? "Entrar" : "Criar Conta"}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-fast ease-smooth" />
                </>
              )}
            </Button>

            {/* R4 — Aviso de aceite, visível apenas no modo cadastro.
                Links abrem em nova aba pra não perder o conteúdo já digitado. */}
            {!isLogin && (
              <p
                className="text-micro text-foreground-subtle leading-relaxed text-center"
                data-testid="auth-legal-notice"
              >
                Ao criar a conta, você concorda com os{" "}
                <Link
                  to="/termos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-cyan hover:underline"
                  data-testid="auth-link-termos"
                >
                  Termos de Uso
                </Link>{" "}
                e a{" "}
                <Link
                  to="/privacidade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-cyan hover:underline"
                  data-testid="auth-link-privacidade"
                >
                  Política de Privacidade
                </Link>
                .
              </p>
            )}
          </form>

          {/* Switch mode */}
          <div className="mt-6 text-center">
            <button
              onClick={switchMode}
              className="text-caption text-foreground-subtle hover:text-brand-cyan transition-colors duration-fast ease-smooth"
              data-testid="auth-switch-mode"
            >
              {isLogin ? (
                <>Não tem conta? <span className="text-brand-cyan font-medium">Criar conta</span></>
              ) : (
                <>Já tem conta? <span className="text-brand-cyan font-medium">Entrar</span></>
              )}
            </button>
          </div>

          {/* Security badge */}
          <div className="mt-8 flex items-center justify-center gap-2 text-foreground-disabled">
            <Shield className="w-3 h-3" />
            <p className="text-micro">Autenticação segura com JWT criptografado</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
